import { cn } from "@/lib/utils"

/**
 * Orbiting-planets loader shown while a report purchase is charging.
 * Geometry, colors and gradients are lifted 1:1 from the Figma "Loader_Stars"
 * (frame Reports_module_purchase_processing): a central purple burst with six
 * planets on two concentric orbits that revolve in opposite directions.
 */
export function ReportOrbitSpinner({ className }: { className?: string }) {
  return (
    <svg
      viewBox="97 240 220 220"
      className={cn("size-52", className)}
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      aria-hidden="true"
    >
      <defs>
        <linearGradient id="rps-burst" x1="207" y1="310" x2="207" y2="390" gradientUnits="userSpaceOnUse">
          <stop stopColor="#DFCBF5" stopOpacity="0.8" />
          <stop offset="1" stopColor="#9565FD" stopOpacity="0.4" />
        </linearGradient>
        <linearGradient id="rps-green" x1="207" y1="281" x2="207" y2="291" gradientUnits="userSpaceOnUse">
          <stop stopColor="#A9FF46" />
          <stop offset="1" stopColor="#5AECCF" />
        </linearGradient>
        <linearGradient id="rps-yellow" x1="262" y1="377" x2="262" y2="387" gradientUnits="userSpaceOnUse">
          <stop stopColor="#FFB727" />
          <stop offset="1" stopColor="#FFF847" />
        </linearGradient>
        <linearGradient id="rps-cyan" x1="130" y1="300" x2="130" y2="314" gradientUnits="userSpaceOnUse">
          <stop stopColor="#00E1FF" />
          <stop offset="1" stopColor="#00FFB3" />
        </linearGradient>
        <linearGradient id="rps-magenta" x1="231" y1="431" x2="231" y2="439" gradientUnits="userSpaceOnUse">
          <stop stopColor="#D942FF" />
          <stop offset="1" stopColor="#FF2A71" />
        </linearGradient>
      </defs>

      {/* Orbit tracks — static dotted rings (Figma "Loader_Stars" → Ellipse 291/292),
          which the earlier SVG export dropped. Center (207,350); inner r=64 with 43 dots,
          outer r=88 with 62 dots; ~1px round dots in #DFCBF5 @ 0.8. A near-zero dash with
          round caps renders each dash as a dot; the gap = circumference/count − dash. */}
      <circle
        cx="207"
        cy="350"
        r="64"
        fill="none"
        stroke="#DFCBF5"
        strokeOpacity="0.8"
        strokeWidth="1"
        strokeLinecap="round"
        strokeDasharray="0.01 9.342"
      />
      <circle
        cx="207"
        cy="350"
        r="88"
        fill="none"
        stroke="#DFCBF5"
        strokeOpacity="0.8"
        strokeWidth="1"
        strokeLinecap="round"
        strokeDasharray="0.01 8.908"
      />

      {/* Central burst — disc with an 8-point star cut out (static). */}
      <path
        d="M207 310C229.091 310 247 327.909 247 350C247 372.091 229.091 390 207 390C184.909 390 167 372.091 167 350C167 327.909 184.909 310 207 310ZM207 333.598L199.275 326L197.448 336.729L186.775 335.168L191.545 344.932L182 350L191.545 355.068L186.775 364.832L197.448 363.271L199.275 374L207 366.402L214.725 374L216.552 363.271L227.225 364.832L222.455 355.068L232 350L222.455 344.932L227.225 335.168L216.552 336.729L214.725 326L207 333.598Z"
        fill="url(#rps-burst)"
      />

      {/* Inner orbit (r≈64) — clockwise. */}
      <g>
        <animateTransform
          attributeName="transform"
          type="rotate"
          from="0 207 350"
          to="360 207 350"
          dur="8s"
          repeatCount="indefinite"
        />
        <circle cx="207" cy="286" r="5" fill="url(#rps-green)" />
        <circle cx="262.426" cy="382" r="12" stroke="#DFCBF5" strokeOpacity="0.4" />
        <circle cx="262.426" cy="382" r="5" fill="url(#rps-yellow)" />
        <circle cx="151.574" cy="382" r="3" fill="#DFCBF5" fillOpacity="0.7" />
      </g>

      {/* Outer orbit (r≈88) — counter-clockwise. */}
      <g>
        <animateTransform
          attributeName="transform"
          type="rotate"
          from="360 207 350"
          to="0 207 350"
          dur="12s"
          repeatCount="indefinite"
        />
        <circle cx="130.328" cy="306.809" r="16" stroke="#DFCBF5" strokeOpacity="0.4" />
        <circle cx="130.328" cy="306.809" r="7" fill="url(#rps-cyan)" />
        <circle cx="281.784" cy="303.617" r="3" fill="#DFCBF5" fillOpacity="0.7" />
        <circle cx="231.061" cy="434.647" r="4" fill="url(#rps-magenta)" />
      </g>
    </svg>
  )
}
